import { createSlice } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import axiosInstance from '../../utils/axios';
import amplifyConfig from '../../config';
import { APPROVAL_VIEW_MODES } from '../../constants';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { AppThunk } from '../store';
import type { ErrorResponse } from '../../types/common';
import type { ApprovalViewMode, ApproveType, ApprovalState } from '../../types/approval';
import type { ConditionDBType, ConditionWithDetailType, UpdateConditionParams } from '../../types/condition';

const initialState: ApprovalState = {
  isLoading: false,
  hasError: false,
  serverMessage: undefined,
  dialogMode: undefined,
  isDialogOpen: false,
  selectedViewMode: APPROVAL_VIEW_MODES.CONFIRM,
  selectedOrganizationId: undefined,
  confirmList: undefined,
  conditionHistory: undefined,
  confirmCondition: undefined,
};

const slice = createSlice({
  name: 'Approval',
  initialState,
  reducers: {
    startLoading(state: ApprovalState) {
      state.isLoading = true;
    },

    hasError(state: ApprovalState, action: PayloadAction<string>) {
      // console.log(action.payload.response);
      const message = action.payload;
      state.isLoading = false;
      state.hasError = true;
      state.serverMessage = message;
    },

    resetMessage(state: ApprovalState) {
      state.isLoading = false;
      state.serverMessage = undefined;
      state.hasError = false;
    },

    setServerMessage(state: ApprovalState, action: PayloadAction<string>): void {
      state.isLoading = false;
      state.serverMessage = action.payload;
    },

    openDialog(state: ApprovalState): void {
      state.isDialogOpen = true;
    },

    closeDialog(state: ApprovalState): void {
      state.isDialogOpen = false;
      state.dialogMode = undefined;
      state.confirmCondition = undefined;
    },

    setSelectedViewMode(state: ApprovalState, action: PayloadAction<ApprovalViewMode>): void {
      state.selectedViewMode = action.payload;
    },

    setOrganizationId(state: ApprovalState, action: PayloadAction<number | undefined>): void {
      state.selectedOrganizationId = action.payload;
    },

    getConfirmCondition(state: ApprovalState, action: PayloadAction<ConditionWithDetailType>): void {
      state.isLoading = false;
      state.confirmCondition = action.payload;
    },

    getConfirmList(state: ApprovalState, action: PayloadAction<ApproveType[]>): void {
      state.isLoading = false;
      state.confirmList = action.payload;
    },

    getConditionHistoryList(state: ApprovalState, action: PayloadAction<ApproveType[]>): void {
      state.isLoading = false;
      state.conditionHistory = action.payload;
    },

    updateCondition(state: ApprovalState, action: PayloadAction<ConditionDBType>): void {
      state.isLoading = false;
      state.confirmCondition = Object.assign({}, state.confirmCondition, action.payload);
    },
  },
});

export const { reducer, actions } = slice;

const asyncErrorHandler = (err, dispatch) => {
  if (err.isAxiosError) {
    // err: AxiosError
    if (err.response) {
      const serverResponse: ErrorResponse = err.response.data;
      dispatch(slice.actions.hasError(serverResponse.message));
    } else {
      dispatch(slice.actions.hasError(err.message));
    }
  } else {
    // err: Error
    dispatch(slice.actions.hasError(err.message));
  }
};

export const resetMessage =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.resetMessage());
  };

export const showErrorMessage =
  (message: string): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.hasError(message));
  };

export const openDialog =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.openDialog());
  };

export const closeDialog =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.closeDialog());
  };

export const setSelectedViewMode =
  (mode: ApprovalViewMode): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.setSelectedViewMode(mode));
  };

export const setOrganizationId =
  (organizationId?: number): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.setOrganizationId(organizationId));
  };

export const getConfirmCondition =
  (id: number): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());

    try {
      const response: AxiosResponse<ConditionWithDetailType> = await axiosInstance.get(amplifyConfig.apiGateway.URL + '/condition/withDetail/' + String(id));
      dispatch(slice.actions.getConfirmCondition(response.data));
    } catch (err) {
      asyncErrorHandler(err, dispatch);
    }
  };

export const getConfirmList =
  (): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());

    try {
      const response: AxiosResponse<ApproveType[]> = await axiosInstance.get(amplifyConfig.apiGateway.URL + '/condition/waitForApprove');
      dispatch(slice.actions.getConfirmList(response.data));
    } catch (err) {
      asyncErrorHandler(err, dispatch);
    }
  };

export const getConditionHistoryList =
  (organizationId?: number): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());

    try {
      const endpoint = organizationId !== undefined ? '/condition/approve/' + String(organizationId) : '/condition/approve/';
      const response: AxiosResponse<ApproveType[]> = await axiosInstance.get(amplifyConfig.apiGateway.URL + endpoint);
      dispatch(slice.actions.getConditionHistoryList(response.data));
    } catch (err) {
      asyncErrorHandler(err, dispatch);
    }
  };

export const updateCondition =
  (id: number, params: UpdateConditionParams): AppThunk =>
  async (dispatch): Promise<void> => {
    // console.log(params);
    dispatch(slice.actions.startLoading());
    try {
      const response: AxiosResponse<ConditionDBType> = await axiosInstance.put(amplifyConfig.apiGateway.URL + '/condition/' + String(id), params);
      dispatch(slice.actions.updateCondition(response.data));
    } catch (err) {
      asyncErrorHandler(err, dispatch);
    }
  };

export default slice;
